import { Dispatch, FunctionComponent, SetStateAction, useCallback, useEffect, useRef } from "react";

import { Box, Grid } from "@mui/material";
import { Nullable } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { FormProvider, useWatch } from "react-hook-form";

import { RecipientEditor } from "../../features/Contact/RecipientEditor";
import { BasicEmailScheduler } from "../BasicEmailScheduler/BasicEmailScheduler";
import { useRecipientPlaceholder } from "../EmailRecipientEditor/useRecipientPlaceholder";

import { StripeEmailAlert } from "./Components/EmailAlert/StripeEmailAlert";
import { VerifyEmailAlert } from "./Components/EmailAlert/VerifyEmailAlert";
import { EmailEditorTour } from "./Components/EmailEditorTour";
import { EmailFooter } from "./Components/EmailFooter/EmailFooter";
import { EmailPreview } from "./Components/EmailPreview/EmailPreview";
import { EmailRecipientLabel } from "./Components/EmailRecipients/EmailRecipientLabel";
import { EmailRecipientsField } from "./Components/EmailRecipients/EmailRecipientsField";
import { EmailRecipientsFieldRef } from "./Components/EmailRecipients/types";
import { useEmailEditor } from "./hooks/useEmailEditor";
import { useEmailEditorForm } from "./hooks/useEmailEditorForm";
import { useEmailEditorTour } from "./hooks/useEmailEditorTour";
import { EmailContent } from "./types";
import { useStyles } from "./useStyles";

import { OutlinedMailFieldWysiwyg } from "@/components/OutlinedMailFieldWysiwyg/OutlinedMailFieldWysiwyg";
import { AmplitudeEvents } from "@/constants/amplitude";
import { DashboardDialog } from "@/dashboard-design-system/Dialog/DashboardDialog";
import { DashboardDrawer } from "@/dashboard-design-system/Drawer";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { DashboardZeffyLoader } from "@/dashboard-design-system/ZeffyLoader/DashboardZeffyLoader";
import { EmailWithFormSchedulerWrapper } from "@/features/EmailEdition/EmailScheduler/EmailWithFormSchedulerWrapper";
import { isRequired } from "@/helpers/validators";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useTranslate } from "@/hooks/useTranslate";
import { ArrowLeft } from "@/icons/outlined";
import { useCurrentUserContext } from "@/src/contexts/CurrentUserContext";

export type EmailEditorProps = {
  onClose: () => void;
  refetch: () => Promise<void>;
  "data-test"?: string;
  setIsFormDirty: Dispatch<SetStateAction<boolean>>;
  setOpenSaveChangesDialog: Dispatch<SetStateAction<boolean>>;
  openSaveChangesDialog: boolean;
  previewOpened: boolean;
  openPreview: () => void;
  closePreview: () => void;
  setRecipientsCount: Dispatch<SetStateAction<number>>;
  inputOnly?: boolean;
  emailContent: EmailContent;
};

export const EmailEditor: FunctionComponent<React.PropsWithChildren<EmailEditorProps>> = (props) => {
  const { onClose, refetch, previewOpened, closePreview, setRecipientsCount, emailContent, setIsFormDirty } = props;
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const classes = useStyles();
  const { currentUser, isSendingEmailForbidden } = useCurrentUserContext();
  const subjectRef = useRef<HTMLInputElement | null>(null);
  const { showRecipientsTour, showTemplatesTour } = useEmailEditorTour();
  const params = useSearchParams();
  const thankYouTransactionId = params.get("thankYouTransactionId") as string;

  const emailRecipientFieldRef = useRef<Nullable<EmailRecipientsFieldRef>>(null);
  const recipientsRef = useRef<HTMLDivElement>(null);
  const templatesRef = useRef<HTMLElement | null>(null);

  const { form, formDefaultValues, handleBodyChange } = useEmailEditorForm({ emailContent });
  const {
    handleSave,
    handleSendTestEmail,
    handleSendEmail,
    handleSchedule,
    loading,
    isSchedulerOpen,
    handleOpenScheduler,
    handleCloseScheduler,
  } = useEmailEditor({
    form,
    refetch,
    onClose,
    emailContent,
  });

  const updateRecipientEmails = useCallback(
    async (validEmails: string[]) => {
      const newEmails = validEmails.map((email) => email.toLowerCase());
      form.setValue("recipientEmails", newEmails, { shouldDirty: true });
    },
    [form]
  );

  const body = useWatch({ control: form.control, name: "body" });
  const subject = useWatch({ control: form.control, name: "subject" });
  const senderName = useWatch({ control: form.control, name: "senderName" });
  const recipientEmails = useWatch({ control: form.control, name: "recipientEmails" });
  const recipientsCount = recipientEmails?.length || 0;

  useEffect(() => {
    setIsFormDirty(form.formState.isDirty);
  }, [form.formState.isDirty, setIsFormDirty]);

  useEffect(() => {
    setRecipientsCount(recipientsCount);
  }, [recipientsCount, setRecipientsCount]);

  const recipientPlaceholder = useRecipientPlaceholder({
    emailType: emailContent?.emailType,
    hasRecipients: Boolean(recipientsCount),
  });

  if (!emailContent) {
    return <EmailLoader />;
  }

  return (
    <>
      <FormProvider {...form}>
        {emailContent?.id && emailContent.emailType && emailContent.formId && isSchedulerOpen && (
          <EmailWithFormSchedulerWrapper
            isSchedulerOpen={isSchedulerOpen}
            emailType={emailContent.emailType}
            closeEmailScheduler={async (_success: boolean) => {
              handleCloseScheduler();
            }}
            formId={emailContent.formId}
            emailContentId={emailContent.id}
            refetch={refetch}
          />
        )}
        {emailContent?.id && !emailContent.emailType && isSchedulerOpen && (
          <BasicEmailScheduler
            isSchedulerOpen={isSchedulerOpen}
            emailContentId={emailContent.id}
            emailSchedulerCategory="basic"
            closeEmailScheduler={handleCloseScheduler}
            closeEmailEditor={() => {}}
            refetch={refetch}
            onSubmit={handleSchedule}
          />
        )}
        <Grid container sx={{ height: "100%" }}>
          <Grid item xs={previewOpened && !isSmallScreen ? 6 : undefined} height="100%" maxWidth="100%">
            <Grid container direction="column" sx={{ height: "100%", flexWrap: "nowrap" }}>
              <Grid item xs sx={{ overflowY: "auto" }}>
                <Grid container height="100%" direction="column" flexWrap="nowrap" paddingLeft={2} paddingRight={2}>
                  <Grid item style={{ maxWidth: "100%" }}>
                    <Grid container>
                      {isSendingEmailForbidden && !currentUser?.emailVerified ? (
                        <Grid item xs={12} marginBottom={1}>
                          <VerifyEmailAlert needToLinkBankAccount />
                        </Grid>
                      ) : (
                        <>
                          {!currentUser?.emailVerified && (
                            <Grid item xs={12} marginBottom={1}>
                              <VerifyEmailAlert />
                            </Grid>
                          )}
                          {isSendingEmailForbidden && (
                            <Grid item xs={12} marginBottom={1}>
                              <StripeEmailAlert />
                            </Grid>
                          )}
                        </>
                      )}

                      <Grid item xs={12} marginBottom={2}>
                        <DashboardTextField
                          name="senderName"
                          defaultValue={formDefaultValues.senderName}
                          control={form.control}
                          debounced={250}
                          label={t("dashboard", "EmailEditor.senderName")}
                          tooltipText={t("dashboard", "emailDrawer.organization.tooltip", {
                            email: currentUser?.email,
                          })}
                        />
                      </Grid>

                      <Grid item xs={12} marginBottom={3}>
                        {recipientPlaceholder && (
                          <EmailRecipientLabel hiddenHelperText>
                            <RecipientEditor placeholder={recipientPlaceholder} />
                          </EmailRecipientLabel>
                        )}
                        {!recipientPlaceholder && (
                          <EmailRecipientsField
                            recipientEmails={recipientEmails || []}
                            updateRecipientEmails={updateRecipientEmails}
                            emailContentId={emailContent.id}
                            ref={emailRecipientFieldRef}
                            inputContainerRef={recipientsRef}
                            onDrawerOpen={() => closePreview()}
                            disabled={Boolean(thankYouTransactionId)}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs sx={{ width: "100%" }} paddingBottom={2}>
                    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                      <Grid item marginBottom={2}>
                        <DashboardTextField
                          data-test="email-editor-subject-input"
                          name="subject"
                          defaultValue={formDefaultValues.subject}
                          control={form.control}
                          debounced={250}
                          errorMessage={form.formState.errors["subject"]?.message}
                          inputProps={{ ref: subjectRef }}
                          label={t("dashboard", "EmailEditor.subject")}
                        />
                      </Grid>
                      <div style={{ flex: 1 }}>
                        <OutlinedMailFieldWysiwyg
                          data-test="email-editor-body-input"
                          className={classes.wysiwyg}
                          keyName="mailFieldModal"
                          defaultValue={formDefaultValues.body}
                          name="body"
                          subjectName="subject"
                          subjectRef={subjectRef}
                          // This makes sure the body is updated when set from
                          // templates within the wysiwyg.
                          onChange={handleBodyChange}
                          height="100%"
                          heightMax="100%"
                          heightMinOver310={310}
                          addVariableAmplitudeEvent={AmplitudeEvents.EmailEditorAddVariableClicked}
                          rules={[isRequired(t("common", "bodyRequired") as string)]}
                          error={form.formState.errors["body"]}
                          canShowTemplateInfobox={!showTemplatesTour && !showRecipientsTour}
                          zIndex={10000}
                          attachmentKeyName="attachment"
                        />
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                paddingTop={2}
                paddingLeft={2}
                paddingRight={2}
                width="100%"
                sx={(theme) => ({ borderTop: 1, borderColor: theme.palette.border.neutral.quiet })}
              >
                <EmailFooter
                  form={form}
                  handleSave={handleSave}
                  handleSendTestEmail={handleSendTestEmail}
                  handleSendEmail={handleSendEmail}
                  handleSchedule={handleOpenScheduler}
                  closeEmailEditor={onClose}
                  loading={loading}
                  emailType={emailContent.emailType}
                />
              </Grid>
            </Grid>
          </Grid>

          {previewOpened && !isSmallScreen && (
            <Grid item xs={6} paddingTop={3} paddingLeft={4} sx={{ height: "100%", overflowY: "auto" }}>
              <EmailPreview
                body={body || ""}
                subject={subject || ""}
                senderName={senderName || ""}
                emailContentId={emailContent.id}
                recipientEmails={typeof recipientEmails === "string" ? [] : recipientEmails || []}
              />
            </Grid>
          )}
        </Grid>
        {previewOpened && isSmallScreen && (
          <DashboardDrawer
            open={previewOpened}
            onClose={closePreview}
            title={t("dashboard", "preview")}
            slots={{
              closeIcon: <ArrowLeft />,
            }}
            closePlacement="left"
          >
            <Grid container justifyContent="center" height="100%">
              <EmailPreview
                body={body || ""}
                subject={subject || ""}
                senderName={senderName || ""}
                emailContentId={emailContent.id}
                recipientEmails={typeof recipientEmails === "string" ? [] : recipientEmails || []}
              />
            </Grid>
          </DashboardDrawer>
        )}
        <EmailEditorTour recipientsRef={recipientsRef} templatesRef={templatesRef} />
        <DashboardDialog
          hideIcon
          maxWidth="xs"
          open={props.openSaveChangesDialog}
          // eslint-disable-next-line react/jsx-no-bind
          onClose={() => props.setOpenSaveChangesDialog(false)}
          title={
            <DashboardTypography variant="h5">
              {t("dashboard", "EmailDrawer.saveChangesDialog.title.saveEmailAsDraft")}
            </DashboardTypography>
          }
          actions={{
            main: {
              children: t("dashboard", "EmailDrawer.saveChangesDialog.save"),
              onClick: handleSave,
            },
            cancel: {
              children: t("dashboard", "EmailDrawer.saveChangesDialog.notSave"),
              onClick: onClose,
            },
          }}
        >
          <Grid>
            <DashboardTypography variant="body2">
              {t("dashboard", "EmailDrawer.saveChangesDialog.content.fromDraftTable")}
            </DashboardTypography>
          </Grid>
        </DashboardDialog>
      </FormProvider>
    </>
  );
};

export const EmailLoader = () => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        height: "100%",
      }}
    >
      <DashboardZeffyLoader size="large" />
    </Box>
  );
};
